import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAmbulanceAuth } from '@/contexts/AmbulanceAuthContext';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface ProtectedAmbulanceRouteProps {
  children: ReactNode;
}

const ProtectedAmbulanceRoute = ({ children }: ProtectedAmbulanceRouteProps) => {
  const { isAuthenticated, isLoading, isAdminSession, providerToken, logout } = useAmbulanceAuth();
  const location = useLocation();
  const { toast } = useToast();

  // Sessions created before token-based auth was added have no stored token —
  // any provider-authenticated API call will 401. Force a fresh login instead
  // of letting every page fail silently one at a time. Admin "Login As"
  // impersonation never has a provider token by design, so it's excluded.
  const isStaleSession = !isLoading && isAuthenticated && !isAdminSession && !providerToken;
  useEffect(() => {
    if (isStaleSession) {
      logout();
      toast({ title: "Session Expired", description: "Please log in again to continue.", variant: "destructive" });
    }
  }, [isStaleSession]);

  if (isLoading || isStaleSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-red-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow admin sessions or authenticated users
  if (!isAuthenticated && !isAdminSession) {
    // Check localStorage for admin impersonation
    const impersonation = localStorage.getItem('patakare_impersonation');
    if (impersonation) {
      try {
        const parsed = JSON.parse(impersonation);
        if (parsed.adminSession && parsed.entityType === 'ambulance_provider') {
          return <>{children}</>;
        }
      } catch {}
    }
    return <Navigate to="/ambulance-provider/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedAmbulanceRoute;
